import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, Eye, Copy, Check, AlertCircle, ChevronDown } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { SendSessionWebhookResponse } from '../../interfaces/payloads';
import { getServicesManager } from '../../services';
import { getSessionState } from '../../utils/session';
import { updateSessionStateField } from '../../lib/redux/sessions/data/data-slice';

interface WebhookPreviewModalProps {
  sessionId: string;
  webhookId?: string;
  isOpen: boolean;
  onClose: () => void;
  sessionState?: Record<string, string>; // Optional session state to use if not in Redux
}

export const WebhookPreviewModal: React.FC<WebhookPreviewModalProps> = ({
  sessionId,
  webhookId: initialWebhookId,
  isOpen,
  onClose,
  sessionState: passedSessionState,
}) => {
  const [selectedWebhookId, setSelectedWebhookId] = useState<string>(initialWebhookId || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [webhookData, setWebhookData] = useState<SendSessionWebhookResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dispatch = useDispatch();

  // Get all webhooks and registries from Redux
  const { webhooks, registries } = useSelector((state: RootState) => state.payloadManagement.sessionWebhooks);

  // Get the session details from Redux to access its state
  const sessionDetails = useSelector((state: RootState) =>
    state.sessionDetails.sessionIdToDetails[sessionId]
  );

  // Get session state to find which webhooks were triggered
  // Use passed state if provided, otherwise get from Redux
  const sessionState = useMemo(() => {
    if (passedSessionState) return passedSessionState;
    if (!sessionDetails?.data?.session_state?.states) return {};
    return sessionDetails.data.session_state.states;
  }, [sessionDetails, passedSessionState]);

  // Extract webhook IDs and their statuses from session state
  const webhookStatuses = useMemo(() => {
    const statuses: Record<string, string> = {};
    Object.keys(sessionState).forEach(key => {
      if (key.startsWith('__webhook_') && key.endsWith('__')) {
        const match = key.match(/__webhook_(.+)__/);
        if (match) {
          const webhookId = match[1];
          statuses[webhookId] = sessionState[key];
        }
      }
    });
    return statuses;
  }, [sessionState]);

  // Show ALL team webhooks (both triggered and untriggered)
  const availableWebhooks = useMemo(() => {
    return webhooks.map(webhook => {
      const webhookId = webhook.hook_id || webhook.webhook_id || '';

      // Extract domain from URL
      let domain = '';
      try {
        const url = new URL(webhook.url || '');
        domain = url.hostname;
      } catch (e) {
        domain = webhook.url || webhookId.substring(0, 12);
      }

      const status = webhookStatuses[webhookId];
      const isTriggered = webhookId in webhookStatuses;

      return {
        id: webhookId,
        name: domain || `Webhook ${webhookId.substring(0, 8)}`,
        url: webhook.url || '',
        status: status ? parseInt(status) : undefined,
        isTriggered
      };
    });
  }, [webhooks, webhookStatuses]);

  // Set initial webhook from prop or default to first available - ONLY ON OPEN
  useEffect(() => {
    if (isOpen && availableWebhooks.length > 0 && !selectedWebhookId) {
      // If initialWebhookId is provided and exists in available webhooks, use it
      if (initialWebhookId && availableWebhooks.some(w => w.id === initialWebhookId)) {
        setSelectedWebhookId(initialWebhookId);
      } else {
        // Otherwise, default to the first available webhook
        setSelectedWebhookId(availableWebhooks[0].id);
      }
    }
  }, [isOpen, initialWebhookId, availableWebhooks]);

  useEffect(() => {
    if (isOpen && selectedWebhookId) {
      loadWebhookPreview();
    } else {
      // Reset state when modal closes
      setWebhookData(null);
      setError(null);
      setCopied(false);
    }
  }, [isOpen, sessionId, selectedWebhookId]);

  const loadWebhookPreview = async () => {
    if (!selectedWebhookId) return;

    setIsLoading(true);
    setError(null);

    try {
      const servicesManager = getServicesManager();
      const response = await servicesManager.sendSessionWebhook({
        session_id: sessionId,
        webhook_id: selectedWebhookId,
        preview: true,
      });

      if (response) {
        setWebhookData(response);
      } else {
        setError('Failed to load webhook preview');
      }
    } catch (err: any) {
      console.error('Error loading webhook preview:', err);
      const errorMessage = err?.response?.data || err?.message || 'Failed to load webhook preview';
      setError(`Error: ${errorMessage}. This session may not have state data saved yet, or the webhook configuration may be incorrect.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendWebhook = async () => {
    if (!selectedWebhookId) return;

    setIsSending(true);
    setError(null);

    try {
      const servicesManager = getServicesManager();
      const response = await servicesManager.sendSessionWebhook({
        session_id: sessionId,
        webhook_id: selectedWebhookId,
        preview: false,
      });

      if (response) {
        setWebhookData(response);

        // Update session state with webhook status
        // The backend returns 'status' field when preview=false
        if (response.status !== undefined && response.status !== null) {
          const webhookStateKey = `__webhook_${selectedWebhookId}__`;
          dispatch(updateSessionStateField({
            sessionId: sessionId,
            key: webhookStateKey,
            value: response.status.toString()
          }));
        }
      } else {
        setError('Failed to send webhook');
      }
    } catch (err: any) {
      console.error('Error sending webhook:', err);
      const errorMessage = err?.response?.data || err?.message || 'Failed to send webhook';
      setError(`Error: ${errorMessage}`);
    } finally {
      setIsSending(false);
    }
  };

  const handleCopyPayload = () => {
    if (!webhookData) return;

    const payload = {
      url: webhookData.url,
      method: webhookData.http_verb,
      headers: webhookData.headers,
      data: webhookData.data,
    };

    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const selectedWebhook = availableWebhooks.find(w => w.id === selectedWebhookId);

  if (!isOpen) return null;

  const modalContent = (
    <>
      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .webhook-modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          z-index: 10000;
          background-color: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(8px);
        }
      `}</style>
      <div
        className="webhook-modal-backdrop"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-xl shadow-2xl flex flex-row overflow-hidden"
          style={{
            animation: 'fadeInScale 0.2s ease-out',
            width: '1200px',
            height: '800px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
        {/* Left Sidebar - Webhook List */}
        <div className="w-80 border-r border-gray-200 bg-gray-50 flex flex-col">
          <div className="px-4 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <h3 className="text-lg font-bold text-gray-900">Webhooks</h3>
          </div>
          <div className="flex-1 overflow-y-auto">
            {availableWebhooks.map((webhook) => (
              <button
                key={webhook.id}
                onClick={() => setSelectedWebhookId(webhook.id)}
                className={`w-full px-4 py-3 text-left transition-colors border-b border-gray-200 ${
                  webhook.id === selectedWebhookId
                    ? 'bg-blue-100 border-l-4 border-l-blue-500'
                    : 'hover:bg-gray-100'
                }`}
              >
                <div className="flex flex-col gap-1">
                  <div className="font-medium text-gray-900 text-sm truncate">{webhook.name}</div>
                  <div className="text-xs text-gray-500 truncate">{webhook.url}</div>
                  <div className="flex items-center gap-2 mt-1">
                    {!webhook.isTriggered && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-200 text-gray-600">
                        Not triggered
                      </span>
                    )}
                    {webhook.status !== undefined && (
                      <span
                        className={`px-2 py-0.5 text-xs font-semibold rounded ${
                          webhook.status >= 200 && webhook.status < 300
                            ? 'bg-green-100 text-green-800'
                            : webhook.status >= 300 && webhook.status < 400
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {webhook.status}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Right Side - Content */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <h2 className="text-2xl font-bold text-gray-900">
              Webhook Inspector
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-50 rounded-lg transition-colors"
            >
              <X size={24} className="text-gray-600" />
            </button>
          </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {!selectedWebhookId ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <AlertCircle size={48} className="mb-4" />
              <p className="text-lg font-medium">No webhooks available for this session</p>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 font-medium">Loading webhook data...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center gap-3 p-4 bg-red-50 text-red-800 rounded-lg border border-red-200">
              <AlertCircle size={24} className="flex-shrink-0" />
              <span className="font-medium">{error}</span>
            </div>
          ) : webhookData ? (
            <div className="space-y-4">
              {/* Request Details */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-4 py-3">
                  <h3 className="text-base font-semibold text-white">Request Details</h3>
                </div>
                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-3">
                    <span className="text-sm font-semibold text-gray-600">Method:</span>
                    <span className="text-sm font-mono bg-blue-100 text-blue-800 px-3 py-1 rounded inline-flex items-center w-fit">
                      {webhookData.http_verb}
                    </span>

                    <span className="text-sm font-semibold text-gray-600">URL:</span>
                    <span className="text-sm font-mono text-gray-900 break-all bg-gray-50 px-3 py-1 rounded">
                      {webhookData.url}
                    </span>

                    {webhookData.encoding && (
                      <>
                        <span className="text-sm font-semibold text-gray-600">Encoding:</span>
                        <span className="text-sm font-mono text-gray-700 bg-gray-50 px-3 py-1 rounded inline-flex items-center w-fit">
                          {webhookData.encoding}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Headers */}
              {Object.keys(webhookData.headers).length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-4 py-3">
                    <h3 className="text-base font-semibold text-white">Headers</h3>
                  </div>
                  <div className="p-4">
                    <pre className="text-xs font-mono text-gray-900 whitespace-pre-wrap break-all bg-gray-50 p-4 rounded border border-gray-200 overflow-x-auto">
                      {JSON.stringify(webhookData.headers, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Body/Data */}
              {webhookData.data && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-4 py-3">
                    <h3 className="text-base font-semibold text-white">Request Body</h3>
                  </div>
                  <div className="p-4">
                    <pre className="text-xs font-mono text-gray-900 whitespace-pre-wrap break-all bg-gray-50 p-4 rounded border border-gray-200 overflow-x-auto max-h-96">
                      {typeof webhookData.data === 'string'
                        ? webhookData.data
                        : JSON.stringify(webhookData.data, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Response (if webhook was sent) */}
              {webhookData.status !== undefined && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className={`px-4 py-3 ${
                    webhookData.status >= 200 && webhookData.status < 300
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                      : 'bg-gradient-to-r from-red-500 to-rose-500'
                  }`}>
                    <h3 className="text-base font-semibold text-white">Response</h3>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-gray-600">Status Code:</span>
                      <span
                        className={`text-base font-mono font-bold px-3 py-1 rounded ${
                          webhookData.status >= 200 && webhookData.status < 300
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {webhookData.status}
                      </span>
                    </div>
                    {webhookData.text && (
                      <div>
                        <span className="text-sm font-semibold text-gray-600 block mb-2">Response Body:</span>
                        <pre className="text-xs font-mono text-gray-900 whitespace-pre-wrap break-all bg-gray-50 p-4 rounded border border-gray-200 overflow-x-auto max-h-64">
                          {webhookData.text}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
          <button
            onClick={handleCopyPayload}
            disabled={!webhookData || isLoading}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-md"
          >
            {copied ? (
              <>
                <Check size={18} className="text-green-600" />
                <span className="text-green-600">Copied!</span>
              </>
            ) : (
              <>
                <Copy size={18} />
                Copy Payload
              </>
            )}
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={loadWebhookPreview}
              disabled={isLoading || isSending || !selectedWebhookId}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-md"
            >
              <Eye size={18} />
              {isLoading ? 'Loading...' : 'Refresh Preview'}
            </button>

            <button
              onClick={handleSendWebhook}
              disabled={!webhookData || isLoading || isSending || !selectedWebhookId}
              className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-lg"
            >
              <Send size={18} />
              {isSending ? 'Sending...' : 'Send Webhook'}
            </button>
          </div>
        </div>
        </div>
      </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
};
